import { useEffect, useMemo, useState } from 'react';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { ErrorState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import { formatTimestampUs, shortId } from '../../lib/format';
import { roleLabel } from '../users/userModel';
import '../_shared/shared.css';
import './customerFamilies.css';
import type { ClientApp, DeveloperOrgRef, Family, FamilyMode, MigrationPreflightResult } from './customerFamiliesApi';
import {
  assignCustomerFamily,
  generateAppKey,
  hashAppKey,
  listCustomerFamilies,
  preflightCustomerFamilyMigration,
  reactivateClientApp,
  revokeClientApp,
  rotateClientAppKey,
  setClientAppEcosystem,
  setClientAppVersionPolicy,
  suspendCustomerFamily,
} from './customerFamiliesApi';
import { listEcosystems } from '../ecosystem/ecosystemApi';
import type { Ecosystem } from '../ecosystem/ecosystemApi';

const RESERVED_FAMILIES = new Set(['fam-platform', 'fam-devmgmt']);
// 保留管理面 client 不可撤銷(08 §2.1.3,防管理台自鎖;後端亦擋 CLIENT_APP_PROTECTED)
const PROTECTED_CLIENTS = new Set(['c-ls-devportal', 'c-ls-admin']);

function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.message}${e.requestId ? `（request_id: ${e.requestId}）` : ''}`;
  return e instanceof Error ? e.message : '操作失敗';
}

function stateTone(state?: string): string {
  if (state === 'active' || state === 'approved') return 'pill-good';
  if (state === 'suspended' || state === 'revoked') return 'pill-warn';
  return 'pill-neutral';
}

function stateLabel(state?: string): string {
  if (!state) return '未知';
  if (state === 'active') return '啟用中';
  if (state === 'approved') return '已核准';
  if (state === 'suspended') return '已暫停';
  if (state === 'revoked') return '已撤銷';
  return state;
}

// 平台家族顯示成品牌名(fam-platform → Kinio.AI),與 /users・/devices 一致
function familyDisplayName(f: Family): string {
  if (f.family_id === 'fam-platform') return f.name || 'Kinio.AI';
  return f.name || shortId(f.family_id);
}

// 「已加入平台池」推導:某 org 以此家族為本源(source_family_id),但現行生效池
// (default_family_id)是另一個共用池。此時家族不是「已暫停」——它是可逆疊加的
// 本源,停止加入即回到此池。回傳該 org(沒有則 null)。
export function joinedPlatformOrg(f: Family, orgs: DeveloperOrgRef[]): DeveloperOrgRef | null {
  return (
    orgs.find(
      (o) => o.source_family_id === f.family_id && !!o.default_family_id && o.default_family_id !== f.family_id
    ) ?? null
  );
}

function effectiveFamilyLabel(familyId: string, families: Family[]): string {
  const f = families.find((x) => x.family_id === familyId);
  return f ? familyDisplayName(f) : familyId;
}

function ownerLabel(ownerScope?: string): string {
  if (!ownerScope) return '—';
  if (ownerScope === 'platform') return '平台';
  if (ownerScope.startsWith('org#')) return ownerScope.slice(4);
  return ownerScope;
}

// eco-kinio-ai 的 row display_name 是小寫 "kinio.ai";顯示端統一成品牌字 "Kinio.AI"。
function ecoLabel(e: Ecosystem): string {
  return e.ecosystem_id === 'eco-kinio-ai' ? 'Kinio.AI' : e.display_name || e.ecosystem_id;
}

const ECO_CLEAR = '__clear__'; // sentinel select value → clear ecosystem_id (empty)

function parseCapabilities(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function checkStatusLabel(status: string): string {
  return status === 'pass' ? '通過' : '未通過';
}

export default function CustomerFamiliesPage() {
  const { role } = useAuth();
  const writable = canAdminWrite(role);
  const [families, setFamilies] = useState<Family[]>([]);
  const [clientApps, setClientApps] = useState<ClientApp[]>([]);
  const [orgs, setOrgs] = useState<DeveloperOrgRef[]>([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState('fam-platform');
  const [orgFilter, setOrgFilter] = useState(''); // server-side (org dropdown)
  const [stateFilter, setStateFilter] = useState(''); // client-side
  const [search, setSearch] = useState(''); // client-side name/id substring
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [err, setErr] = useState('');
  const [loadErr, setLoadErr] = useState<unknown>(null);

  // on-demand drawers
  const [assignOpen, setAssignOpen] = useState(false);
  const [preflightOpen, setPreflightOpen] = useState(false);

  // assign form
  const [customerRef, setCustomerRef] = useState('');
  const [familyMode, setFamilyMode] = useState<FamilyMode>('platform_family');
  const [platformFamilyId, setPlatformFamilyId] = useState('fam-platform');
  const [familyName, setFamilyName] = useState('');
  const [clientId, setClientId] = useState('');
  const [appKeyHash, setAppKeyHash] = useState('');
  const [capabilities, setCapabilities] = useState('');
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState('');

  // preflight form
  const [preflightClientId, setPreflightClientId] = useState('');
  const [preflightTargetFamily, setPreflightTargetFamily] = useState('');
  const [preflightReason, setPreflightReason] = useState('');
  const [preflightCaseId, setPreflightCaseId] = useState('');
  const [preflight, setPreflight] = useState<MigrationPreflightResult | null>(null);

  // suspend family
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendCaseId, setSuspendCaseId] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);

  // app_key one-time reveal
  const [pendingAppKey, setPendingAppKey] = useState('');
  const [revealedKey, setRevealedKey] = useState<{ clientId: string; appKey: string; kind: 'assign' | 'rotate' } | null>(null);

  // client lifecycle (drawer opens when a client is selected)
  const [selectedClientId, setSelectedClientId] = useState('');
  const [lifecycleReason, setLifecycleReason] = useState('');
  const [lifecycleCaseId, setLifecycleCaseId] = useState('');
  const [rotateWindowDays, setRotateWindowDays] = useState('90');
  const [policyMin, setPolicyMin] = useState('');
  const [policyForce, setPolicyForce] = useState('');
  const [policyDeprecate, setPolicyDeprecate] = useState(false);
  // client → ecosystem 對映(restful#84):可設 ecosystem inventory + 抽屜內選取的目標
  const [ecosystems, setEcosystems] = useState<Ecosystem[]>([]);
  const [ecoTarget, setEcoTarget] = useState('');

  async function load() {
    setBusy(true);
    setErr('');
    setLoadErr(null);
    try {
      const data = await listCustomerFamilies({ orgId: orgFilter, limit: 100 });
      setFamilies(data.families);
      setClientApps(data.client_apps);
      setOrgs(data.customer_orgs);
      if (!customerRef && data.customer_orgs[0]) setCustomerRef(`org:${data.customer_orgs[0].org_id}`);
      if (!preflightTargetFamily && data.families[0]) setPreflightTargetFamily(data.families[0].family_id);
      if (data.families.length > 0 && !data.families.some((f) => f.family_id === selectedFamilyId)) {
        setSelectedFamilyId(data.families[0].family_id);
      }
    } catch (e) {
      setLoadErr(e);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgFilter]);

  // active ecosystem inventory for the client→ecosystem dropdown (best-effort).
  useEffect(() => {
    let alive = true;
    listEcosystems({ status: 'active', limit: 200 })
      .then((r) => alive && setEcosystems(r))
      .catch(() => {
        /* best-effort; dropdown just stays sparse */
      });
    return () => {
      alive = false;
    };
  }, []);

  function selectFamily(familyId: string) {
    setSelectedFamilyId(familyId);
    setNotice('');
    setErr('');
    setSelectedClientId(''); // close any open lifecycle drawer when switching family
  }

  const platformFamilies = useMemo(
    () => families.filter((f) => f.owner_scope === 'platform' && f.state === 'active' && f.family_id !== 'fam-devmgmt'),
    [families]
  );
  const selectedOrg = useMemo(() => orgs.find((org) => `org:${org.org_id}` === customerRef) ?? null, [orgs, customerRef]);
  const selectedFamily = useMemo(() => families.find((f) => f.family_id === selectedFamilyId) ?? null, [families, selectedFamilyId]);
  const selectedClients = useMemo(
    () => clientApps.filter((app) => (app.family_id || 'fam-platform') === selectedFamilyId),
    [clientApps, selectedFamilyId]
  );
  const totalCapabilities = useMemo(() => new Set(clientApps.flatMap((app) => app.capabilities ?? [])).size, [clientApps]);

  // client-side master list filter (state + name/id search)
  const visibleFamilies = useMemo(() => {
    const q = search.trim().toLowerCase();
    return families.filter((f) => {
      if (stateFilter === 'joined') {
        if (!joinedPlatformOrg(f, orgs)) return false;
      } else if (stateFilter && f.state !== stateFilter) return false;
      if (!q) return true;
      return (
        familyDisplayName(f).toLowerCase().includes(q) ||
        f.family_id.toLowerCase().includes(q) ||
        (f.owner_scope || '').toLowerCase().includes(q)
      );
    });
  }, [families, orgs, stateFilter, search]);

  const assignReady = Boolean(
    writable &&
      customerRef.trim() &&
      clientId.trim() &&
      appKeyHash.trim() &&
      reason.trim() &&
      caseId.trim() &&
      (familyMode === 'owned_family' ? familyName.trim() : platformFamilyId.trim())
  );
  const preflightReady = Boolean(
    writable && preflightClientId.trim() && preflightTargetFamily.trim() && preflightReason.trim() && preflightCaseId.trim()
  );
  const suspendReady = Boolean(
    writable && selectedFamily && !RESERVED_FAMILIES.has(selectedFamily.family_id) && suspendReason.trim() && suspendCaseId.trim()
  );

  async function generateKeyIntoForm() {
    const key = generateAppKey();
    const hash = await hashAppKey(key);
    setPendingAppKey(key);
    setAppKeyHash(hash);
  }

  async function submitAssign() {
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await assignCustomerFamily({
        customer_ref: customerRef.trim(),
        family_mode: familyMode,
        platform_family_id: familyMode === 'platform_family' ? platformFamilyId.trim() : undefined,
        family_name: familyMode === 'owned_family' ? familyName.trim() : undefined,
        client_id: clientId.trim(),
        app_key_hash: appKeyHash.trim(),
        capabilities: parseCapabilities(capabilities),
        reason: reason.trim(),
        case_id: caseId.trim(),
      });
      setFamilies((rows) => (rows.some((f) => f.family_id === res.family.family_id) ? rows : [res.family, ...rows]));
      setClientApps((rows) => [res.client_app, ...rows.filter((app) => app.client_id !== res.client_app.client_id)]);
      setOrgs((rows) =>
        rows.map((org) =>
          `org:${org.org_id}` === customerRef && !org.default_family_id ? { ...org, default_family_id: res.family.family_id } : org
        )
      );
      setSelectedFamilyId(res.family.family_id);
      if (pendingAppKey) {
        setRevealedKey({ clientId: res.client_app.client_id, appKey: pendingAppKey, kind: 'assign' });
        setPendingAppKey('');
      }
      setClientId('');
      setAppKeyHash('');
      setFamilyName('');
      setCapabilities('');
      setReason('');
      setCaseId('');
      setAssignOpen(false);
      setNotice(`已建立 ${res.client_app.client_id} 並指派到 ${res.family.family_id}`);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  // --- client lifecycle (08 §2.1.3) ---
  const selectedClient = useMemo(() => clientApps.find((app) => app.client_id === selectedClientId) ?? null, [clientApps, selectedClientId]);
  const lifecycleReady = Boolean(writable && selectedClient && lifecycleReason.trim() && lifecycleCaseId.trim());

  function patchClientApp(next: ClientApp) {
    setClientApps((rows) => rows.map((app) => (app.client_id === next.client_id ? next : app)));
  }

  function openLifecycle(app: ClientApp) {
    setSelectedClientId(app.client_id);
    setLifecycleReason('');
    setLifecycleCaseId('');
    setPolicyMin('');
    setPolicyForce('');
    setPolicyDeprecate(false);
    setEcoTarget(app.ecosystem_id ?? '');
    setErr('');
  }

  async function submitSetEcosystem() {
    if (!selectedClient) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await setClientAppEcosystem(selectedClient.client_id, {
        ecosystem_id: ecoTarget,
        reason: lifecycleReason.trim(),
        case_id: lifecycleCaseId.trim(),
      });
      patchClientApp(res);
      const label = ecoTarget
        ? ecosystems.find((e) => e.ecosystem_id === ecoTarget)
          ? ecoLabel(ecosystems.find((e) => e.ecosystem_id === ecoTarget) as Ecosystem)
          : ecoTarget
        : '（已清除）';
      setNotice(`已將 ${res.client_id} 的生態系設為 ${label}(新註冊將繼承)`);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitRotateKey() {
    if (!selectedClient) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const key = generateAppKey();
      const hash = await hashAppKey(key);
      const days = Number.parseInt(rotateWindowDays, 10);
      const res = await rotateClientAppKey(selectedClient.client_id, {
        new_app_key_hash: hash,
        previous_key_window_days: Number.isFinite(days) && days > 0 ? days : undefined,
        reason: lifecycleReason.trim(),
        case_id: lifecycleCaseId.trim(),
      });
      patchClientApp(res);
      setRevealedKey({ clientId: res.client_id, appKey: key, kind: 'rotate' });
      setSelectedClientId(''); // 關掉生命週期抽屜,讓一次性 key 揭示卡在主畫面可見(不被 overlay 蓋住)
      setNotice(`已輪替 ${res.client_id} 的 App Key(舊 key 於雙 key 窗到期後失效)`);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitRevoke() {
    if (!selectedClient) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await revokeClientApp(selectedClient.client_id, { reason: lifecycleReason.trim(), case_id: lifecycleCaseId.trim() });
      patchClientApp(res);
      setNotice(`已撤銷 ${res.client_id}(即時生效)`);
      setConfirmRevoke(false);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitReactivate() {
    if (!selectedClient) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await reactivateClientApp(selectedClient.client_id, { reason: lifecycleReason.trim(), case_id: lifecycleCaseId.trim() });
      patchClientApp(res);
      setNotice(`已復用 ${res.client_id}`);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitVersionPolicy() {
    if (!selectedClient) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await setClientAppVersionPolicy(selectedClient.client_id, {
        min_supported_version: policyMin.trim(),
        force_upgrade_below: policyForce.trim(),
        deprecated_at: policyDeprecate ? Date.now() * 1000 : undefined,
        reason: lifecycleReason.trim(),
        case_id: lifecycleCaseId.trim(),
      });
      patchClientApp(res);
      setNotice(`已更新 ${res.client_id} 的版本政策`);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitPreflight() {
    setBusy(true);
    setErr('');
    setNotice('');
    setPreflight(null);
    try {
      const res = await preflightCustomerFamilyMigration({
        client_id: preflightClientId.trim(),
        target_family_id: preflightTargetFamily.trim(),
        reason: preflightReason.trim(),
        case_id: preflightCaseId.trim(),
      });
      setPreflight(res);
      setNotice(`已完成 ${res.client_id} 的遷移預檢`);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitSuspend() {
    if (!selectedFamily) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await suspendCustomerFamily(selectedFamily.family_id, { reason: suspendReason.trim(), case_id: suspendCaseId.trim() });
      setFamilies((rows) => rows.map((f) => (f.family_id === res.family_id ? res : f)));
      setNotice(`已暫停 ${res.family_id}`);
      setConfirmSuspend(false);
      setSuspendReason('');
      setSuspendCaseId('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const isReserved = selectedFamily ? RESERVED_FAMILIES.has(selectedFamily.family_id) : false;

  return (
    <div className="org-page cf-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">客戶與品牌家族</h1>
          <p className="page-subtitle">品牌家族(生態系認證池)與其客戶端應用(App 憑證)的掛載與生命週期。</p>
        </div>
        <div className="adm-actionbar-btns">
          <span className={`pill ${writable ? 'pill-good' : 'pill-neutral'}`}>{role ? roleLabel(role) : '其他角色'}</span>
          <button className="btn-ghost btn-sm" onClick={() => void load()} disabled={busy}>
            {busy ? '同步中' : '重新整理'}
          </button>
          {writable && (
            <button className="btn-primary btn-sm" onClick={() => { setErr(''); setAssignOpen(true); }}>
              ＋ 指派客戶端
            </button>
          )}
        </div>
      </div>

      {notice && (
        <p className="adm-action-result is-ok" role="status">
          {notice}
        </p>
      )}
      {err && !assignOpen && !preflightOpen && !selectedClientId && (
        <p className="adm-action-result is-err" role="alert">
          {err}
        </p>
      )}

      {/* app_key 一次性揭示:原文只在此存在,關閉即丟(後端只存 hash) */}
      {revealedKey && (
        <section className="card cf-reveal-card" role="alertdialog" aria-label="應用金鑰一次性揭示">
          <div className="section-head">
            <h2 className="section-title">
              {revealedKey.kind === 'rotate' ? '新 App Key(輪替)' : 'App Key(新建)'} — {revealedKey.clientId}
            </h2>
          </div>
          <p className="cf-reveal-warn">⚠️ 這是唯一一次顯示。請立即複製並交付到對應 App build 管線;關閉後平台只保留雜湊、無法再取回原文。</p>
          <div className="cf-reveal-row">
            <code className="cf-reveal-key adm-mono">{revealedKey.appKey}</code>
            <button
              className="btn-ghost btn-sm"
              onClick={() => {
                void navigator.clipboard.writeText(revealedKey.appKey);
                setNotice('已複製 App Key 原文');
              }}
            >
              複製
            </button>
            <button className="btn-danger btn-sm" onClick={() => setRevealedKey(null)}>
              我已保存,關閉
            </button>
          </div>
        </section>
      )}

      <div className="kpi-strip" aria-label="客戶家族統計">
        <div className="kpi-card kpi-card--hero">
          <span className="kpi-label">家族</span>
          <span className="kpi-value">{families.length}</span>
          <span className="kpi-foot">{families.filter((f) => f.owner_scope === 'platform').length} 個平台家族</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">客戶端應用</span>
          <span className="kpi-value">{clientApps.length}</span>
          <span className="kpi-foot">{clientApps.filter((a) => a.status === 'active').length} 個啟用中</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">客戶組織</span>
          <span className="kpi-value">{orgs.length}</span>
          <span className="kpi-foot">已核准開發者組織</span>
        </div>
        <div className="kpi-card kpi-card--secondary">
          <span className="kpi-label">能力</span>
          <span className="kpi-value">{totalCapabilities}</span>
          <span className="kpi-foot">不重複客戶端範圍</span>
        </div>
      </div>

      {/* ---- master–detail:左家族清單 / 右選取家族的客戶端與操作 ---- */}
      <div className="cf-md">
        {/* MASTER */}
        <section className="card cf-master">
          <div className="section-head">
            <h2 className="section-title">家族清單</h2>
            <span className="section-meta text-tertiary">{visibleFamilies.length}/{families.length}</span>
          </div>
          <div className="cf-master-filters">
            <label className="adm-field">
              <span className="adm-field-label text-secondary">組織</span>
              <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)} disabled={busy}>
                <option value="">全部組織</option>
                {orgs.map((org) => (
                  <option key={org.org_id} value={org.org_id}>
                    {org.name || org.slug || org.org_id}
                  </option>
                ))}
              </select>
            </label>
            <label className="adm-field">
              <span className="adm-field-label text-secondary">狀態</span>
              <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                <option value="">全部狀態</option>
                <option value="active">啟用中</option>
                <option value="joined">已加入平台池</option>
                <option value="suspended">已暫停</option>
              </select>
            </label>
            <label className="adm-field cf-master-search">
              <span className="adm-field-label text-secondary">搜尋</span>
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="家族名稱 / id / 擁有者" />
            </label>
          </div>
          {loadErr ? (
            <ErrorState error={loadErr} onRetry={() => void load()} />
          ) : visibleFamilies.length > 0 ? (
            <ul className="cf-fam-list">
              {visibleFamilies.map((f) => {
                const count = clientApps.filter((app) => (app.family_id || 'fam-platform') === f.family_id).length;
                return (
                  <li key={f.family_id}>
                    <button
                      className={`cf-fam-item ${selectedFamilyId === f.family_id ? 'is-selected' : ''}`}
                      onClick={() => selectFamily(f.family_id)}
                    >
                      <span className="cf-fam-main">
                        <span className="cf-fam-name">{familyDisplayName(f)}</span>
                        <span className="cf-fam-id adm-mono text-tertiary" title={f.family_id}>{f.family_id}</span>
                      </span>
                      <span className="cf-fam-meta">
                        {(() => {
                          const joined = joinedPlatformOrg(f, orgs);
                          if (joined) {
                            const eff = effectiveFamilyLabel(joined.default_family_id!, families);
                            return (
                              <span
                                className="pill pill-good"
                                title={`本源家族;現行生效池=${joined.default_family_id}(可逆,停止加入即回到本家族)`}
                              >
                                已加入 {eff}
                              </span>
                            );
                          }
                          return <span className={`pill ${stateTone(f.state)}`}>{stateLabel(f.state)}</span>;
                        })()}
                        <span className="cf-fam-count" title="客戶端應用數(綁定此家族的 App 憑證)">{count} 客戶端</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="empty-state cf-empty-fill">
              <div className="empty-state-icon">🗂️</div>
              <div className="empty-state-title">沒有符合條件的家族</div>
              <div className="empty-state-detail">調整組織／狀態／搜尋,或指派第一個客戶端。</div>
              {writable && (
                <div className="empty-state-actions">
                  <button className="btn-ghost btn-sm" onClick={() => setAssignOpen(true)}>指派客戶端</button>
                </div>
              )}
            </div>
          )}
        </section>

        {/* DETAIL */}
        <section className="card cf-detail">
          {selectedFamily ? (
            <>
              <div className="cf-detail-head">
                <div className="cf-detail-title">
                  <h2 className="section-title">{familyDisplayName(selectedFamily)}</h2>
                  <span className="adm-mono text-tertiary">{selectedFamily.family_id}</span>
                </div>
                <div className="cf-detail-actions">
                  {joinedPlatformOrg(selectedFamily, orgs) ? (
                    <span className="pill pill-good">
                      已加入 {effectiveFamilyLabel(joinedPlatformOrg(selectedFamily, orgs)!.default_family_id!, families)}
                    </span>
                  ) : (
                    <span className={`pill ${stateTone(selectedFamily.state)}`}>{stateLabel(selectedFamily.state)}</span>
                  )}
                  {writable && (
                    <>
                      <button
                        className="btn-ghost btn-sm"
                        onClick={() => {
                          setPreflightTargetFamily(selectedFamily.family_id);
                          setPreflight(null);
                          setErr('');
                          setPreflightOpen(true);
                        }}
                      >
                        遷移預檢
                      </button>
                      <button
                        className="btn-danger btn-sm"
                        disabled={isReserved}
                        title={isReserved ? '系統保留家族不可暫停' : undefined}
                        onClick={() => { setErr(''); setSuspendReason(''); setSuspendCaseId(''); setConfirmSuspend(true); }}
                      >
                        暫停家族
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="cf-detail-facts">
                <span className="text-tertiary">擁有者</span>
                <span title={selectedFamily.owner_scope}>{ownerLabel(selectedFamily.owner_scope)}</span>
                <span className="text-tertiary">客戶端</span>
                <span>{selectedClients.length} 個</span>
                <span className="text-tertiary">更新時間</span>
                <span className="adm-mono">{formatTimestampUs(selectedFamily.updated_at || selectedFamily.created_at, { second: undefined })}</span>
              </div>
              {(() => {
                const joined = joinedPlatformOrg(selectedFamily, orgs);
                if (!joined) return null;
                return (
                  <p className="text-secondary cf-joined-note">
                    此家族是 {joined.name || joined.org_id} 的<strong>本源帳號池</strong>;現行生效池為{' '}
                    <span className="adm-mono">{joined.default_family_id}</span>(
                    {effectiveFamilyLabel(joined.default_family_id!, families)})。加入平台池是可逆疊加——停止加入即回到本家族,
                    因此客戶端數為 0 屬預期,並非停用。
                  </p>
                );
              })()}

              <div className="cf-detail-sub">
                <h3 className="section-title">客戶端應用</h3>
                <span className="text-tertiary">{writable ? '點選一列管理生命週期(輪替 / 版本政策 / 撤銷)' : '唯讀檢視'}</span>
              </div>
              {selectedClients.length > 0 ? (
                <div className="table-wrap cf-table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>客戶端</th>
                        <th>狀態</th>
                        <th>生態系</th>
                        <th>能力</th>
                        <th>更新時間</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedClients.map((app) => (
                        <tr
                          key={app.client_id}
                          className={`clickable ${selectedClientId === app.client_id ? 'is-selected' : ''}`}
                          onClick={() => openLifecycle(app)}
                        >
                          <td className="adm-mono" title={app.client_id}>{app.client_id}</td>
                          <td>
                            <span className={`pill ${stateTone(app.status)}`}>{stateLabel(app.status)}</span>
                          </td>
                          <td>
                            {app.ecosystem_id ? (
                              <span
                                className={`pill pill-${app.ecosystem_id === 'eco-kinio-ai' ? 'neutral' : 'info'}`}
                                title={app.ecosystem_id}
                              >
                                {ecosystems.find((e) => e.ecosystem_id === app.ecosystem_id)
                                  ? ecoLabel(ecosystems.find((e) => e.ecosystem_id === app.ecosystem_id) as Ecosystem)
                                  : app.ecosystem_id}
                              </span>
                            ) : (
                              <span className="text-tertiary">—</span>
                            )}
                          </td>
                          <td>
                            {(app.capabilities ?? []).length > 0 ? (
                              <div className="cf-cap-cell">
                                {(app.capabilities ?? []).slice(0, 6).map((cap) => (
                                  <span key={cap} className="pill pill-neutral">{cap}</span>
                                ))}
                                {(app.capabilities ?? []).length > 6 && (
                                  <span className="text-tertiary">+{(app.capabilities ?? []).length - 6}</span>
                                )}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="adm-mono">{formatTimestampUs(app.updated_at || app.created_at, { second: undefined })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state empty-state--inline cf-workbench-empty">
                  <div className="empty-state-icon">📭</div>
                  <div className="empty-state-title">此家族目前沒有客戶端</div>
                  <div className="empty-state-detail">{writable ? '用右上「指派客戶端」建立。' : '尚無資料。'}</div>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state cf-empty-fill">
              <div className="empty-state-icon">👈</div>
              <div className="empty-state-title">選一個家族</div>
              <div className="empty-state-detail">從左側清單選取家族,查看其客戶端應用與操作。</div>
            </div>
          )}
        </section>
      </div>

      {/* ---- 指派客戶端 抽屜 ---- */}
      <Drawer
        open={assignOpen}
        title="指派客戶端"
        subtitle="把一個 App 憑證(client_app)掛到品牌家族;首次指派會設為該組織的預設生態系。"
        busy={busy}
        onClose={() => setAssignOpen(false)}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setAssignOpen(false)} disabled={busy}>取消</button>
            <button className="btn-primary" disabled={busy || !assignReady} onClick={() => void submitAssign()}>建立指派</button>
          </>
        }
      >
        {err && assignOpen && <p className="adm-action-result is-err" role="alert">{err}</p>}
        <div className="cf-form">
          <label className="adm-field">
            <span className="adm-field-label text-secondary">客戶組織</span>
            <select id="cf-assign-org-select" value={customerRef} onChange={(e) => setCustomerRef(e.target.value)} disabled={busy}>
              {orgs.map((org) => (
                <option key={org.org_id} value={`org:${org.org_id}`}>{org.name || org.slug || org.org_id}</option>
              ))}
              {orgs.length === 0 && <option value="">無已核准組織</option>}
            </select>
            <span className="cf-hint text-tertiary">
              預設生態系：{selectedOrg?.default_family_id || '尚未設定；本次核可後設為預設'}。新增第二個以上生態系不會取代預設值。
            </span>
          </label>
          <div className="segmented-control" aria-label="家族模式">
            <button className={familyMode === 'platform_family' ? 'is-active' : ''} onClick={() => setFamilyMode('platform_family')}>平台家族</button>
            <button className={familyMode === 'owned_family' ? 'is-active' : ''} onClick={() => setFamilyMode('owned_family')}>自有家族</button>
          </div>
          {familyMode === 'platform_family' ? (
            <label className="adm-field">
              <span className="adm-field-label text-secondary">平台家族</span>
              <select value={platformFamilyId} onChange={(e) => setPlatformFamilyId(e.target.value)} disabled={busy}>
                {platformFamilies.map((f) => (
                  <option key={f.family_id} value={f.family_id}>{familyDisplayName(f)}</option>
                ))}
              </select>
            </label>
          ) : (
            <label className="adm-field">
              <span className="adm-field-label text-secondary">自有家族名稱</span>
              <input value={familyName} onChange={(e) => setFamilyName(e.target.value)} placeholder="Living Style" disabled={busy} />
            </label>
          )}
          <label className="adm-field">
            <span className="adm-field-label text-secondary">客戶端 ID</span>
            <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="c-living-style-web" disabled={busy} />
          </label>
          <label className="adm-field">
            <span className="adm-field-label text-secondary">應用金鑰雜湊</span>
            <div className="cf-keygen-row">
              <input
                value={appKeyHash}
                onChange={(e) => { setAppKeyHash(e.target.value); setPendingAppKey(''); }}
                placeholder="sha256 雜湊(base64url)"
                disabled={busy}
              />
              <button className="btn-ghost btn-sm" disabled={busy} onClick={() => void generateKeyIntoForm()} title="瀏覽器端產 32-byte 隨機 key;建立成功後一次性揭示原文">產生</button>
            </div>
            {pendingAppKey && <span className="cf-hint text-tertiary">已產生金鑰,建立成功後一次性顯示原文。</span>}
          </label>
          <label className="adm-field">
            <span className="adm-field-label text-secondary">能力(逗號或換行分隔)</span>
            <textarea value={capabilities} onChange={(e) => setCapabilities(e.target.value)} rows={3} disabled={busy} />
          </label>
          <div className="cf-form-grid">
            <label className="adm-field">
              <span className="adm-field-label text-secondary">原因</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} />
            </label>
            <label className="adm-field">
              <span className="adm-field-label text-secondary">案件編號</span>
              <input value={caseId} onChange={(e) => setCaseId(e.target.value)} disabled={busy} />
            </label>
          </div>
          {!assignReady && <span className="cf-hint text-tertiary">需填妥客戶端 ID、應用金鑰雜湊、家族、原因與案件編號。</span>}
        </div>
      </Drawer>

      {/* ---- 遷移預檢 抽屜 ---- */}
      <Drawer
        open={preflightOpen}
        title="遷移預檢"
        subtitle="檢查把某客戶端搬到目標家族是否可即時套用(不會實際遷移)。"
        tone="neutral"
        busy={busy}
        onClose={() => setPreflightOpen(false)}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setPreflightOpen(false)} disabled={busy}>關閉</button>
            <button className="btn-primary" disabled={busy || !preflightReady} onClick={() => void submitPreflight()}>執行預檢</button>
          </>
        }
      >
        {err && preflightOpen && <p className="adm-action-result is-err" role="alert">{err}</p>}
        <div className="cf-form">
          <label className="adm-field">
            <span className="adm-field-label text-secondary">客戶端應用</span>
            <input value={preflightClientId} onChange={(e) => setPreflightClientId(e.target.value)} placeholder="c-current-web" list="customer-family-client-apps" disabled={busy} />
          </label>
          <label className="adm-field">
            <span className="adm-field-label text-secondary">目標家族</span>
            <select value={preflightTargetFamily} onChange={(e) => setPreflightTargetFamily(e.target.value)} disabled={busy}>
              {families.map((f) => (
                <option key={f.family_id} value={f.family_id}>{familyDisplayName(f)}</option>
              ))}
            </select>
          </label>
          <div className="cf-form-grid">
            <label className="adm-field">
              <span className="adm-field-label text-secondary">原因</span>
              <input value={preflightReason} onChange={(e) => setPreflightReason(e.target.value)} disabled={busy} />
            </label>
            <label className="adm-field">
              <span className="adm-field-label text-secondary">案件編號</span>
              <input value={preflightCaseId} onChange={(e) => setPreflightCaseId(e.target.value)} disabled={busy} />
            </label>
          </div>
          {preflight && (
            <div className="cf-preflight-result">
              <div className="adm-cell-strong adm-mono">{preflight.current_family_id} → {preflight.target_family_id}</div>
              <div className="text-tertiary">即時套用:{preflight.can_apply_inline ? '允許' : '阻擋'}</div>
              <ul className="cf-check-list">
                {preflight.checks.map((check) => (
                  <li key={check.code}>
                    <span className={`pill ${check.status === 'pass' ? 'pill-good' : 'pill-warn'}`}>{checkStatusLabel(check.status)}</span>
                    <span>{check.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Drawer>

      {/* ---- 客戶端生命週期 抽屜 ---- */}
      <Drawer
        open={!!selectedClient}
        title={<span className="adm-mono">{selectedClient?.client_id}</span>}
        subtitle={
          selectedClient ? (
            <>
              狀態:{stateLabel(selectedClient.status)}
              {selectedClient.previous_key_expires_at ? ` · 雙 key 窗至 ${formatTimestampUs(selectedClient.previous_key_expires_at, { second: undefined })}` : ''}
              {selectedClient.min_supported_version ? ` · 建議 ≥ ${selectedClient.min_supported_version}` : ''}
              {selectedClient.force_upgrade_below ? ` · 強制升級 < ${selectedClient.force_upgrade_below}` : ''}
            </>
          ) : undefined
        }
        tone="neutral"
        busy={busy}
        onClose={() => setSelectedClientId('')}
      >
        {err && selectedClientId && <p className="adm-action-result is-err" role="alert">{err}</p>}
        {!writable ? (
          <p className="org-muted">目前角色僅能檢視客戶端狀態。</p>
        ) : (
          <div className="cf-form">
            <div className="cf-form-grid">
              <label className="adm-field">
                <span className="adm-field-label text-secondary">原因</span>
                <input value={lifecycleReason} onChange={(e) => setLifecycleReason(e.target.value)} disabled={busy} />
              </label>
              <label className="adm-field">
                <span className="adm-field-label text-secondary">案件編號</span>
                <input value={lifecycleCaseId} onChange={(e) => setLifecycleCaseId(e.target.value)} disabled={busy} />
              </label>
            </div>
            {!lifecycleReady && <span className="cf-hint text-tertiary">所有操作都需先填原因與案件編號(將寫入稽核)。</span>}

            <div className="cf-lc-op">
              <div className="adm-cell-strong">輪替 App Key</div>
              <p className="text-tertiary">產新 key 並開雙 key 窗;舊 key 窗內仍可用、到期自動失效。新 key 原文一次性揭示。</p>
              <label className="adm-field">
                <span className="adm-field-label text-secondary">雙 key 窗(天,≤180)</span>
                <input value={rotateWindowDays} onChange={(e) => setRotateWindowDays(e.target.value)} disabled={busy} />
              </label>
              <button className="btn-primary btn-sm" disabled={busy || !lifecycleReady || selectedClient?.status === 'revoked'} onClick={() => void submitRotateKey()}>
                產生新 Key 並輪替
              </button>
            </div>

            <div className="cf-lc-op">
              <div className="adm-cell-strong">版本政策</div>
              <div className="cf-form-grid">
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">建議最低版本</span>
                  <input value={policyMin} onChange={(e) => setPolicyMin(e.target.value)} placeholder="2.1.0" disabled={busy} />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">強制升級門檻</span>
                  <input value={policyForce} onChange={(e) => setPolicyForce(e.target.value)} placeholder="1.8.0(≤ 建議最低)" disabled={busy} />
                </label>
              </div>
              <label className="cf-checkbox">
                <input type="checkbox" checked={policyDeprecate} onChange={(e) => setPolicyDeprecate(e.target.checked)} disabled={busy} />
                <span>同時標記為汰除中(active → deprecated)</span>
              </label>
              <button className="btn-ghost btn-sm" disabled={busy || !lifecycleReady || !policyMin.trim() || !policyForce.trim() || selectedClient?.status === 'revoked'} onClick={() => void submitVersionPolicy()}>
                套用版本政策
              </button>
            </div>

            <div className="cf-lc-op">
              <div className="adm-cell-strong">生態系歸屬</div>
              <p className="text-tertiary">
                設定此用戶端應用所屬生態系（`ecosystem_id`）。透過此用戶端應用註冊的新使用者會**繼承**它作為基礎生態系;不影響既有使用者。
              </p>
              <label className="adm-field">
                <span className="adm-field-label text-secondary">生態系</span>
                <select
                  value={ecoTarget === '' ? ECO_CLEAR : ecoTarget}
                  onChange={(e) => setEcoTarget(e.target.value === ECO_CLEAR ? '' : e.target.value)}
                  disabled={busy || selectedClient?.status === 'revoked'}
                >
                  {ecosystems.map((e) => (
                    <option key={e.ecosystem_id} value={e.ecosystem_id}>
                      {ecoLabel(e)}
                    </option>
                  ))}
                  {selectedClient?.ecosystem_id && !ecosystems.some((e) => e.ecosystem_id === selectedClient.ecosystem_id) && (
                    <option value={selectedClient.ecosystem_id}>{selectedClient.ecosystem_id}（目前）</option>
                  )}
                  <option value={ECO_CLEAR}>（清除歸屬）</option>
                </select>
              </label>
              <button
                className="btn-primary btn-sm"
                disabled={busy || !lifecycleReady || selectedClient?.status === 'revoked' || ecoTarget === (selectedClient?.ecosystem_id ?? '')}
                onClick={() => void submitSetEcosystem()}
              >
                設定生態系
              </button>
            </div>

            <div className="cf-lc-op cf-lc-op--danger">
              <div className="adm-cell-strong">{selectedClient?.status === 'revoked' ? '復用' : '撤銷'}</div>
              <p className="text-tertiary">
                {selectedClient?.status === 'revoked'
                  ? '撤銷後 90 天內可復用;逾期永久除役,需另建新客戶端。'
                  : '撤銷即時生效:該 client 的 App 無法再登入或換發 token。'}
              </p>
              {selectedClient?.status === 'revoked' ? (
                <button className="btn-primary btn-sm" disabled={busy || !lifecycleReady} onClick={() => void submitReactivate()}>復用此客戶端</button>
              ) : (
                <button
                  className="btn-danger btn-sm"
                  disabled={busy || !lifecycleReady || (selectedClient ? PROTECTED_CLIENTS.has(selectedClient.client_id) : true)}
                  onClick={() => { setErr(''); setConfirmRevoke(true); }}
                >
                  撤銷此客戶端
                </button>
              )}
              {selectedClient && PROTECTED_CLIENTS.has(selectedClient.client_id) && (
                <span className="cf-hint text-tertiary">系統保留管理面客戶端不可撤銷。</span>
              )}
            </div>
          </div>
        )}
      </Drawer>

      <datalist id="customer-family-client-apps">
        {clientApps.map((app) => (
          <option key={app.client_id} value={app.client_id} />
        ))}
      </datalist>

      {confirmRevoke && selectedClient && (
        <ConfirmDialog
          title="撤銷客戶端 App"
          tone="danger"
          confirmLabel="確認撤銷"
          busy={busy}
          error={err || undefined}
          warning="撤銷即時生效:此 client 的 App 將無法再登入或換發 token。"
          body={
            <>
              <p>將撤銷客戶端 <span className="adm-mono">{selectedClient.client_id}</span>。</p>
              <p className="text-tertiary">處置理由:{lifecycleReason.trim()}・案件:{lifecycleCaseId.trim()}(將寫入稽核)</p>
            </>
          }
          onConfirm={() => void submitRevoke()}
          onCancel={() => setConfirmRevoke(false)}
        />
      )}

      {confirmSuspend && selectedFamily && (
        <ConfirmDialog
          title="暫停客戶家族"
          tone="danger"
          confirmLabel="確認暫停"
          busy={busy}
          confirmDisabled={!suspendReady}
          error={err || undefined}
          warning="暫停家族即時生效:該家族下所有 client 的登入 / token 換發將受影響。"
          body={
            <>
              <p>將暫停家族 <span className="adm-mono">{selectedFamily.family_id}</span>{selectedFamily.name ? `（${selectedFamily.name}）` : ''}。</p>
              <div className="cf-form-grid">
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">原因</span>
                  <input value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)} disabled={busy} />
                </label>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">案件編號</span>
                  <input value={suspendCaseId} onChange={(e) => setSuspendCaseId(e.target.value)} disabled={busy} />
                </label>
              </div>
            </>
          }
          onConfirm={() => void submitSuspend()}
          onCancel={() => setConfirmSuspend(false)}
        />
      )}
    </div>
  );
}
